/**
 * POSPrintFailureAlert - Shows popup alerts when print jobs fail
 * Polls backend for failed print notifications and displays them prominently
 */
import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Printer, X, Check, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function POSPrintFailureAlert() {
  const [failures, setFailures] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Poll for print failures
  const checkForFailures = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/print-failures/pending`);
      if (res.ok) {
        const data = await res.json();
        if (data.failures && data.failures.length > 0) {
          setFailures(data.failures);
          setShowAlert(true);
          
          // Play alert sound for new failures
          if (audioEnabled && data.failures.length > failures.length) {
            playAlertSound();
          }
        }
      }
    } catch (err) {
      console.error('Failed to check print failures:', err);
    }
  }, [failures.length, audioEnabled]);

  // Poll every 5 seconds
  useEffect(() => {
    checkForFailures();
    const interval = setInterval(checkForFailures, 5000);
    return () => clearInterval(interval);
  }, [checkForFailures]);

  // Play alert sound
  const playAlertSound = () => {
    try {
      const audio = new Audio('/alert.mp3');
      audio.volume = 0.7;
      audio.play().catch(() => {
        // Fallback: use system beep via Web Audio API
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'square';
        gain.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => {
          oscillator.stop();
          ctx.close();
        }, 300);
      });
    } catch (e) {
      console.log('Could not play alert sound');
    }
  };

  // Acknowledge a single failure
  const acknowledgeFailure = async (failureId) => {
    try {
      const res = await fetch(`${API_URL}/api/print-failures/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ failure_ids: [failureId] })
      });
      
      if (res.ok) {
        setFailures(prev => prev.filter(f => f.failure_id !== failureId));
        if (failures.length <= 1) {
          setShowAlert(false);
        }
        toast.success('Alert acknowledged');
      }
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
  };

  // Acknowledge all failures
  const acknowledgeAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/print-failures/acknowledge-all`, {
        method: 'POST'
      });
      
      if (res.ok) {
        setFailures([]);
        setShowAlert(false);
        toast.success('All alerts acknowledged');
      }
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
  };

  // Get station display name
  const getStationName = (station) => {
    const names = {
      'customer': 'Customer Receipt',
      'barista': 'Barista',
      'kitchen': 'Kitchen',
      'juicer': 'Juicer',
      'counter': 'Counter',
      'kiosk': 'Kiosk'
    };
    return names[station] || station;
  };

  if (!showAlert || failures.length === 0) {
    return null;
  }

  return (
    <>
      {/* Full Screen Alert Overlay */}
      <div 
        className="fixed inset-0 bg-red-900/90 z-[200] flex items-center justify-center p-4"
        data-testid="print-failure-alert"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-pulse-slow">
          {/* Header */}
          <div className="bg-red-600 text-white p-4 flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">PRINT FAILURE</h2>
              <p className="text-red-100 text-sm">{failures.length} order(s) need manual handling</p>
            </div>
            <button
              onClick={() => setAudioEnabled(!audioEnabled)}
              className="p-2 hover:bg-white/20 rounded-full"
              title={audioEnabled ? 'Mute alerts' : 'Enable alerts'}
            >
              <Bell className={`w-5 h-5 ${audioEnabled ? '' : 'opacity-50'}`} />
            </button>
          </div>

          {/* Failures List */}
          <div className="max-h-80 overflow-y-auto">
            {failures.map((failure, idx) => (
              <div 
                key={failure.failure_id || idx}
                className="p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    {/* Order Number - LARGE */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-3xl font-bold text-red-600">
                        #{failure.order_number || 'N/A'}
                      </span>
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-sm font-medium">
                        {getStationName(failure.station)}
                      </span>
                    </div>
                    
                    {/* Customer Name */}
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      {failure.customer_name || 'Guest'}
                    </p>
                    
                    {/* Error Message */}
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      <Printer className="w-4 h-4 inline mr-1" />
                      {failure.error_message || 'Failed to print after 3 attempts'}
                    </p>
                    
                    {/* Timestamp */}
                    <p className="text-xs text-gray-400 mt-1">
                      {failure.created_at ? new Date(failure.created_at).toLocaleTimeString() : ''}
                    </p>
                  </div>
                  
                  {/* Acknowledge Button */}
                  <Button
                    onClick={() => acknowledgeFailure(failure.failure_id)}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Done
                  </Button>
                </div>
                
                {/* Manual Action Reminder */}
                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
                    ⚠️ Please write this order manually and deliver to {getStationName(failure.station)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Check printer connections and paper
            </p>
            <div className="flex gap-2">
              <Button
                onClick={acknowledgeAll}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Acknowledge All ({failures.length})
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Minimized Alert Badge (when closed but still have failures) */}
      {failures.length > 0 && !showAlert && (
        <button
          onClick={() => setShowAlert(true)}
          className="fixed bottom-4 right-4 z-[100] bg-red-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse"
        >
          <AlertTriangle className="w-5 h-5" />
          {failures.length} Print Failure(s)
        </button>
      )}
    </>
  );
}
